import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { ReputationBadge } from './ReputationBadge';
import ContractPanel, { downloadContractPdf } from './ContractPanel';

/**
 * The contracts drawer — every agreement this account is a party to.
 *
 * The document already existed on each deal card, but reaching one meant
 * remembering which campaign it belonged to, finding that row and expanding
 * an accordion. That is filing, not paperwork. A creator asked for last
 * year's agreement with a brand has one question — *which brand* — so the
 * counterparty is the headline here and the campaign is the subtitle, the
 * opposite of every other campaign surface.
 *
 * Downloading never opens anything: the button on the row fetches the PDF
 * straight away. Anything the row cannot say is a tap away in the deal card,
 * which stays the place where a contract is *read* and countersigned.
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const shortDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
}) : '—');

const STATE_FILTERS = ['', 'open', 'closed'];

function DownloadButton({ row, t, onError }) {
    const { lang } = useTranslation();
    const [busy, setBusy] = useState(false);

    const download = async () => {
        setBusy(true);
        onError('');
        try {
            await downloadContractPdf(row.deal_id, row.reference, lang);
        } catch (err) {
            onError(t('contract.downloadError'));
        } finally { setBusy(false); }
    };

    return (
        <button onClick={download} disabled={busy}
            title={t('contract.downloadBtn')}
            className="flex-shrink-0 px-3 py-2 rounded-xl text-[11px] font-bold text-primary-700 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition flex items-center gap-1.5 disabled:opacity-60">
            {busy
                ? <span className="w-3.5 h-3.5 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                : <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>}
            <span className="hidden sm:inline">PDF</span>
        </button>
    );
}

function ContractRow({ row, t, onError, onAccepted }) {
    const [expanded, setExpanded] = useState(false);
    const counterparty = row.counterparty || {};

    return (
        <div className="rounded-2xl bg-white border border-slate-200 p-3.5 hover:shadow-md transition">
            <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {counterparty.profile_picture
                        ? <img src={counterparty.profile_picture} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-slate-300 text-lg">◎</div>}
                </div>

                <div className="flex-1 min-w-0">
                    {/* Who it was with leads: that is what anyone searching
                        their own contracts is actually looking for. */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-bold text-slate-800 truncate">
                            {counterparty.username}
                        </span>
                        {counterparty.verified && (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-primary-500 flex-shrink-0"
                                viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        )}
                        <ReputationBadge rating={counterparty.rating} />
                        <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-wide">
                            {t(`contracts.role_${row.my_role}`)}
                        </span>
                    </div>

                    <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {row.campaign?.title}
                    </p>

                    <p className="text-[10px] text-slate-400 font-semibold tabular-nums mt-0.5">
                        {row.reference} · {shortDate(row.issued_at)}
                    </p>

                    <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                        {/* Each side sees the figure that was theirs: the brand
                            paid the gross, the creator was promised the net. */}
                        <span className="text-[11px] font-black text-slate-800 tabular-nums">
                            {usd(row.my_amount_usd)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${row.is_open
                            ? 'bg-sky-50 text-sky-700 border-sky-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                            {t(`campaigns.dealStatus_${row.deal_status}`)}
                        </span>
                        {row.fully_accepted && (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold border border-emerald-200">
                                {t('contract.signed')}
                            </span>
                        )}
                        {row.awaiting_me && (
                            <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[9px] font-bold border border-amber-200">
                                {t('contracts.needsYou')}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-1.5 flex-shrink-0">
                    {/* Hidden while the panel is open — it carries its own
                        download, and two buttons for one file is a question,
                        not a convenience. */}
                    {!expanded && <DownloadButton row={row} t={t} onError={onError} />}
                    <button onClick={() => setExpanded(!expanded)}
                        className={`px-3 py-2 rounded-xl text-[11px] font-semibold border transition ${expanded
                            ? 'text-slate-600 bg-slate-100 border-slate-300'
                            : 'text-slate-500 bg-slate-50 border-slate-200 hover:bg-slate-100'}`}>
                        {expanded ? t('contracts.hide') : t('contracts.view')}
                    </button>
                </div>
            </div>

            {/* The same panel the deal card shows — full frozen terms, the
                digest and the countersignature, so the drawer is somewhere a
                contract can be signed and not only fetched. */}
            {expanded && (
                <div className="mt-3">
                    <ContractPanel deal={{ id: row.deal_id }} contract={row}
                        defaultOpen onAccepted={onAccepted} />
                </div>
            )}
        </div>
    );
}

function ContractsList() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [state, setState] = useState('');
    const [role, setRole] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const params = {};
            if (query) params.q = query;
            if (state) params.state = state;
            if (role) params.role = role;
            const res = await api.get('/api/campaigns/contracts/', { params });
            setRows(res.data.contracts || []);
        } catch (err) {
            setError(t('contract.loadError'));
        } finally { setLoading(false); }
    }, [query, state, role, t]);

    useEffect(() => {
        const timer = setTimeout(load, query ? 350 : 0);   // debounce the search box
        return () => clearTimeout(timer);
    }, [load, query]);

    // The role switch only earns its space on an account that has signed from
    // both sides. Most have not, and a filter with one possible answer is noise.
    // Latched, because the control must not disappear out from under the hand
    // that is using it the moment a *different* filter narrows the results.
    const [bothRoles, setBothRoles] = useState(false);
    useEffect(() => {
        if (new Set(rows.map(r => r.my_role)).size > 1) setBothRoles(true);
    }, [rows]);
    const showRoleFilter = bothRoles || !!role;

    const pendingCount = rows.filter(r => r.awaiting_me).length;

    if (loading) {
        return <div className="flex justify-center py-16"><div className="spinner" /></div>;
    }

    const filtering = !!(query || state || role);

    return (
        <div className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed px-1">{t('contracts.hint')}</p>

            {/* Only unfiltered: a count taken from a narrowed list would read
                as a total and quietly understate what is actually waiting. */}
            {!filtering && pendingCount > 0 && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
                    <p className="text-[11px] font-bold text-amber-800">
                        {pendingCount} {t('contracts.needsYouBanner')}
                    </p>
                </div>
            )}

            <div className="rounded-2xl bg-white border border-slate-200 p-3 space-y-2">
                <input className="input py-2 text-sm" value={query}
                    placeholder={t('contracts.searchPlaceholder')}
                    onChange={(e) => setQuery(e.target.value)} />
                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
                    {STATE_FILTERS.map(key => (
                        <button key={key || 'all'} onClick={() => setState(key)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-semibold border flex-shrink-0 transition ${state === key
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-slate-500 border-slate-200'}`}>
                            {t(`contracts.state_${key || 'all'}`)}
                        </button>
                    ))}
                    {showRoleFilter && (
                        <select className="input py-1.5 text-xs w-auto flex-shrink-0" value={role}
                            onChange={(e) => setRole(e.target.value)}>
                            <option value="">{t('contracts.role_any')}</option>
                            <option value="brand">{t('contracts.role_brand')}</option>
                            <option value="creator">{t('contracts.role_creator')}</option>
                        </select>
                    )}
                </div>
            </div>

            {error && <p className="text-xs text-red-600 text-center">{error}</p>}

            {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <p className="text-sm font-bold text-slate-600">
                        {filtering ? t('contracts.emptyFiltered') : t('contracts.empty')}
                    </p>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">
                        {filtering ? t('contracts.emptyFilteredDesc') : t('contracts.emptyDesc')}
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map(row => (
                        <ContractRow key={row.deal_id} row={row} t={t}
                            onError={setError} onAccepted={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default ContractsList;
